import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import TemplateMediaSessionDownloadQuery from './__generated__/TemplateMediaSessionDownloadQuery.graphql.ts';
import type {
	TemplateMediaSessionDownloadQuery as TemplateMediaSessionDownloadQueryType,
	TemplateMediaSessionDownloadQueryVariables,
} from './__types__/TemplateMediaSessionDownloadQuery.ts';
import TemplateMediaSessionUploadQuery from './__generated__/TemplateMediaSessionUploadQuery.graphql.ts';
import type {
	TemplateMediaSessionUploadQuery as TemplateMediaSessionUploadQueryType,
	TemplateMediaSessionUploadQueryVariables,
} from './__types__/TemplateMediaSessionUploadQuery.ts';
import {
	queryInterop,
	type QueryInteropSupportedResult,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/query.ts';

type TokenQuery = {
	spaceKey?: string | null;
	templateIds: string[];
	collectionId?: string | null;
};

/**
 * spacekey - global vs space level
 * templateIds - string[] - for previews of multiple templates
 * want to count UUID as normal template
 * templateIds - string - to differntiate between preview of single template and copy pasting
 * want to count UUID as new template in the case of uploads
 * collectionId - passed since templates that don't have static identifiers until they are saved with an image
 **/
export const getTemplateDownloadToken = async ({
	spaceKey,
	templateIds,
	collectionId = null,
}: TokenQuery): Promise<
	QueryInteropSupportedResult<TemplateMediaSessionDownloadQueryType> | undefined
> => {
	return queryInterop<
		TemplateMediaSessionDownloadQueryType,
		TemplateMediaSessionDownloadQueryVariables
	>({
		query: TemplateMediaSessionDownloadQuery,
		variables: {
			spaceKey: spaceKey || null,
			templateIds,
			collectionId,
		},
		fetchPolicy: 'network-only',
	}).catch((error: Error) => {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PAGE_MODES,
		});
		markErrorAsHandled(error);
		return undefined;
	});
};

export const getTemplateUploadToken = async ({
	spaceKey,
	templateIds,
	collectionId = null,
}: TokenQuery): Promise<
	QueryInteropSupportedResult<TemplateMediaSessionUploadQueryType> | undefined
> => {
	return queryInterop<
		TemplateMediaSessionUploadQueryType,
		TemplateMediaSessionUploadQueryVariables
	>({
		query: TemplateMediaSessionUploadQuery,
		variables: {
			spaceKey: spaceKey || null,
			templateIds,
			collectionId,
		},
		fetchPolicy: 'no-cache',
	}).catch((error: Error) => {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PAGE_MODES,
		});
		markErrorAsHandled(error);
		return undefined;
	});
};
